<template>
  <div v-if="loading" class="page-content">
    <Loading />
  </div>

  <div v-else>
    <div class="strip fs-14 between-flex">
      <button v-if="appData" class="back-button" @click="goBack">
        <span class="material-symbols-outlined"> arrow_back </span>
      </button>

      <form class="search-box" @submit.prevent="onFormSubmit">
        <SearchAndFilter
          v-model="url"
          :placeholder="this.$t('Custom.enterUrl')"
          :showInput="true"
          :showSelect="false"
          @submitForm="onFormSubmit"
        />
      </form>
    </div>

    <div v-if="appData" class="app-details">
      <AppInfo
        :appData="appData"
        :appCountries="appCountries"
        @get-app="fetchData"
      />
    </div>
    <div v-else class="error-message">
      {{ error }}
    </div>
  </div>

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />
</template>

<script>
import SearchAndFilter from "@/components/user/common/SearchAndFilter.vue";
import Loading from "@/components/user/layout/LoadingComponent.vue";
import AppInfo from "@/components/user/viewComponent/app/AppInfo.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import { getApp, getAppCountries } from "@/api/app.api";
import {
  detectPlatform,
  extractPlayStoreCountry,
  extractStoreId,
} from "@/ultis/user/functions/storePlatform";
import PlatformTypeEnum from "@/enums/PlatformTypeEnum";

export default {
  name: "AppsOverviewComponent",
  components: {
    SearchAndFilter,
    Loading,
    AppInfo,
    NotificationComponent,
  },
  props: {
    goBackVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      url: "",
      appData: null,
      appCountries: null,
      loading: false,
      error: null,
    };
  },
  methods: {
    async fetchData(url = this.url) {
      this.loading = true;
      this.appCountries = null;
      this.appData = null;
      try {
        await this.processAppLink(url);
        await this.getAppCountry();
        this.$emit("toggle-visibility", false);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async onFormSubmit() {
      const platform = detectPlatform(this.url);
      if (!platform) {
        this.showNotification(
          "hold",
          this.$t("Custom.hold"),
          this.$t("Custom.pleaseCheckYourUrl")
        );
        return;
      }
      const storeId = extractStoreId(this.url, platform);
      if (!storeId) {
        this.showNotification(
          "hold",
          this.$t("Custom.hold"),
          this.$t("Custom.pleaseCheckYourUrl")
        );
        return;
      }
      
      let country =
        platform === PlatformTypeEnum.PLAY_STORE
          ? extractPlayStoreCountry(this.url)
          : null;
      if (!country) country = "US";

      this.$router.push({
        name: "App",
        params: { storeId },
        query: { country },
      });
      // await this.fetchData();
    },
    async processAppLink(url = this.url) {
      if (!url) return;

      try {
        const response = await getApp(url);
        if (response.data.status) {
          this.appData = response.data.data;
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
          this.clearInput();
        }
      } catch (error) {
        this.loading;
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          response.data.msg
        );
        this.clearInput();
      }
    },
    async getAppCountry() {
      if (this.appCountries && this.appCountries.length > 0) {
        return;
      }

      try {
        const payload =
          this.appData.country.countryShortName === "US"
            ? { appId: this.appData.app.id }
            : { appId: this.appData.app.appId };

        const response = await getAppCountries(payload.appId);

        if (response.data.status) {
          this.appCountries = response.data.data.map((item) => ({
            ...item.country,
            appUrl: item.appUrl,
          }));
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          response.data.msg
        );
      }
    },
    async clearInput() {
      this.url = "";
      this.$emit("toggle-visibility", true);
    },
    goBack() {
      this.appData = null;
      this.url = "";
      this.$emit("toggle-visibility", true);
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
  },
};
</script>

<style scoped>
.search-box {
  width: 100%;
}

.back-button {
  background: none;
  border: none;
  font-size: 16px;
  padding: 7px;
  color: var(--logo-color);
  border-radius: 50%;
  cursor: pointer;
  margin-right: 10px;
}
.back-button:hover {
  background: var(--hover-color);
  text-decoration: underline;
}
.search-box .input-search {
  margin-right: 10px;
}

@media (max-width: 767px) {
  .search-box {
    width: 100%;
  }
}
</style>
